// contexts/ReportsContext.jsx
import React, { createContext, useContext, useState, useCallback } from "react";
import apiService from "../services/api";

const ReportsContext = createContext();

export const useReports = () => {
  const context = useContext(ReportsContext);
  if (!context) {
    throw new Error("useReports must be used within ReportsProvider");
  }
  return context;
};

export const ReportsProvider = ({ children }) => {
  const [reports, setReports] = useState([]);
  const [folders, setFolders] = useState([]);
  const [reportTypes, setReportTypes] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  const [reportResults, setReportResults] = useState(null);

  // Fetch report types
  const fetchReportTypes = useCallback(async () => {
    try {
      const response = await apiService.getReportTypes();
      setReportTypes(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching report types:", error);
      throw error;
    }
  }, []);
  const createSalesReports = async () => {
    try {
      setLoading(true);
      const response = await apiService.createSalesReports();
      await fetchReports(); // Refresh reports list
      return response.data;
    } catch (error) {
      console.error("Failed to create sales reports:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const createCaseReports = async () => {
    try {
      setLoading(true);
      const response = await apiService.createCaseReports();
      await fetchReports(); // Refresh reports list
      return response.data;
    } catch (error) {
      console.error("Failed to create case reports:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const createUpgradeReports = async () => {
    try {
      setLoading(true);
      const response = await apiService.createUpgradeReports();
      await fetchReports(); // Refresh reports list
      return response.data;
    } catch (error) {
      console.error("Failed to create case reports:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // expose it in the context value
  // Fetch folders
  const fetchFolders = useCallback(async () => {
    try {
      const response = await apiService.getReportFolders();
      setFolders(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching folders:", error);
      throw error;
    }
  }, []);

  // Fetch reports
  const fetchReports = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      const response = await apiService.getReports(filters);
      setReports(response.data);
      return response;
    } catch (error) {
      console.error("Error fetching reports:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch single report
  const fetchReport = useCallback(async (reportId) => {
    try {
      setLoading(true);
      const response = await apiService.getReport(reportId);
      setCurrentReport(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching report:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create report
  const createReport = useCallback(async (reportData) => {
    try {
      setLoading(true);
      const response = await apiService.createReport(reportData);
      setReports((prev) => [response.data, ...prev]);
      return response.data;
    } catch (error) {
      console.error("Error creating report:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update report
  const updateReport = useCallback(
    async (reportId, reportData) => {
      try {
        setLoading(true);
        const response = await apiService.updateReport(reportId, reportData);
        setReports((prev) =>
          prev.map((r) => (r.id === reportId ? response.data : r)),
        );
        if (currentReport?.id === reportId) {
          setCurrentReport(response.data);
        }
        return response.data;
      } catch (error) {
        console.error("Error updating report:", error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [currentReport],
  );

  // Delete report
  const deleteReport = useCallback(
    async (reportId) => {
      try {
        setLoading(true);
        await apiService.deleteReport(reportId);
        setReports((prev) => prev.filter((r) => r.id !== reportId));
        if (currentReport?.id === reportId) {
          setCurrentReport(null);
        }
      } catch (error) {
        console.error("Error deleting report:", error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [currentReport],
  );

  // Run report
  const runReport = useCallback(async (reportId, runtimeFilters = {}) => {
    try {
      setLoading(true);
      const response = await apiService.runReport(reportId, runtimeFilters);
      setReportResults(response);
      return response;
    } catch (error) {
      console.error("Error running report:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Export report
  const exportReport = useCallback(
    async (reportId, format = "csv", runtimeFilters = {}) => {
      try {
        const response = await apiService.exportReport(
          reportId,
          format,
          runtimeFilters,
        );
        return response;
      } catch (error) {
        console.error("Error exporting report:", error);
        throw error;
      }
    },
    [],
  );

  // Clone report
  const cloneReport = useCallback(async (reportId, name) => {
    try {
      setLoading(true);
      const response = await apiService.cloneReport(reportId, name);
      setReports((prev) => [response.data, ...prev]);
      return response.data;
    } catch (error) {
      console.error("Error cloning report:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create folder
  const createFolder = useCallback(async (folderData) => {
    try {
      const response = await apiService.createReportFolder(folderData);
      setFolders((prev) => [...prev, response.data]);
      return response.data;
    } catch (error) {
      console.error("Error creating folder:", error);
      throw error;
    }
  }, []);

  // Sync from Salesforce
  const syncFromSalesforce = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.syncSalesforceReports();
      await fetchReports();
      await fetchFolders();
      return response;
    } catch (error) {
      console.error("Error syncing from Salesforce:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchReports, fetchFolders]);

  const value = {
    reports,
    folders,
    reportTypes,
    loading,
    currentReport,
    reportResults,
    setCurrentReport,
    setReportResults,
    fetchReportTypes,
    fetchFolders,
    fetchReports,
    fetchReport,
    createReport,
    updateReport,
    deleteReport,
    runReport,
    exportReport,
    cloneReport,
    createFolder,
    createSalesReports,
    createCaseReports,
    createUpgradeReports,
    syncFromSalesforce,
  };

  return (
    <ReportsContext.Provider value={value}>{children}</ReportsContext.Provider>
  );
};
